import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { BetaTestBanner } from '@/components/BetaTestBanner';

type Props = {
  open: boolean;
  onAccept: () => Promise<void>;
};

/**
 * Blocking modal — erscheint beim Login wenn der User den Beta-Disclaimer
 * noch nicht akzeptiert hat (Spalte profiles.beta_terms_accepted_at IS NULL).
 *
 * Kein Schließen via ESC, kein Backdrop-Click (disableEscapeKeyDown +
 * default-Behavior bei MUI Dialog: Backdrop-Click schließt nicht weil
 * onClose nicht gesetzt). User muss aktiv die Checkbox setzen und
 * „Bestätigen" klicken.
 */
export function BetaTermsModal({ open, onAccept }: Props) {
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);
    setError(null);
    try {
      await onAccept();
    } catch (e) {
      setError(
        e instanceof Error
          ? `Speichern fehlgeschlagen: ${e.message}`
          : 'Speichern fehlgeschlagen — bitte Seite neu laden und erneut versuchen.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      aria-labelledby="beta-terms-title"
    >
      <DialogTitle id="beta-terms-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'warning.lighter',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ScienceOutlinedIcon sx={{ fontSize: 20, color: 'warning.dark' }} />
          </Box>
          <Typography variant="h6" component="span" fontWeight={700} fontSize="1.05rem">
            Beta-Hinweis bestätigen
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Wir haben in der aktuellen Testphase einen Haftungs-Hinweis ergänzt. Bitte
          bestätige diesen einmalig, um ImmoKompass weiter nutzen zu können.
        </Typography>

        <BetaTestBanner variant="full" sx={{ mb: 2 }} />

        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              size="small"
              sx={{ pt: 0.5 }}
            />
          }
          label={
            <Typography
              variant="caption"
              sx={{ fontSize: '0.8rem', lineHeight: 1.45, color: 'text.secondary' }}
            >
              Ich habe verstanden, dass sich ImmoKompass in einer <strong>Testphase</strong>{' '}
              befindet und die KI-Analysen unverbindliche Einschätzungen sind. ImmoKompass{' '}
              <strong>übernimmt keine Haftung</strong> für inhaltliche Fehler oder
              Schlussfolgerungen der KI. Kaufentscheidungen treffe ich auf eigene
              Verantwortung und nach professioneller Beratung.
            </Typography>
          }
          sx={{ alignItems: 'flex-start', m: 0 }}
        />

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          variant="contained"
          disabled={!checked || loading}
          onClick={() => void handleAccept()}
          sx={{ minWidth: 120, textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Wird gespeichert…' : 'Bestätigen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
